import { useState } from 'react'
import styled from 'styled-components'
import { useMutation } from '@apollo/client'
import Editor from '../Editor/Editor'
import Checkbox from '../Checkbox/Checkbox'
import Button from '../Button/Button'
import createTicketComment from '../../graphql/createTicketComment'
import Drawer from '../Drawer/Drawer'
import { quillIndent } from '../../utils'

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;

  & > button {
    align-self: flex-start;
  }

  margin-bottom: 10px;

  & > div {
    flex: 1;
    margin-right: 10px;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--color-bg-sidebar);
  border-radius: 4px;
  width: 642px;
  padding: 50px;
  font-size: 14px;

  @media only screen and (max-width: 920px) {
    width: 70%;
  }

  @media only screen and (max-width: 600px) {
    padding: 25px;
    width: 90%;
  }
`

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 42px;
`

const Title = styled.h1`
  color: var(--color-text-primary);
  font-weight: 900;
  font-size: 24px;
  line-height: 1;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
`

const Icon = styled.div`
  background-color: var(--color-text-secondary);
  width: 3px;
  height: 16px;
  transform: rotate(45deg);
  pointer-events: none;

  &::before {
    content: '';
    display: block;
    background-color: var(--color-text-secondary);

    width: 3px;
    height: 16px;
    transform: rotate(-90deg);
  }

  ${CloseButton}:hover & {
    filter: brightness(110%);
  }
`

const Col = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`

const Label = styled.span`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: var(--color-text-primary);
`

export const ErrorMsg = styled.span`
  color: var(--color-error);
  margin-bottom: 12px;
`

const initialState = {
  values: {
    comment: '',
    solved: false,
  },
  touched: {},
  errors: {},
}

const SupportCaseViewForm = ({ ticketId, isPriority, isOpen, toggle }) => {
  const [createComment] = useMutation(createTicketComment)

  const [form, setForm] = useState(initialState)
  const [isSubmitting, setSubmitting] = useState(false)

  const validate = values => {
    const errors = {}

    if (!values.comment) errors.comment = 'Please fill comment field!'

    return errors
  }

  const onChange = (name, value) => {
    setForm(form => {
      const values = {
        ...form.values,
        [name]: value,
      }

      const errors = validate(values)

      const newForm = {
        values,
        touched: {
          ...form.touched,
          [name]: true,
        },
        errors,
      }

      return newForm
    })
  }

  const resetForm = () => {
    setForm(initialState)
  }

  const onSubmit = async () => {
    try {
      if (isPriority) return
      const errors = validate(form.values)

      if (!!Object.keys(errors).length) {
        const touched = Object.keys(errors).reduce((obj, key) => {
          obj[key] = true
          return obj
        }, {})

        setForm(form => ({
          ...form,
          errors,
          touched,
        }))
        return
      }

      setSubmitting(true)

      const variables = {
        id: ticketId,
        comment: quillIndent(form.values.comment),
      }

      if (form.values.solved) {
        variables.status = 'solved'
      }

      // Add this to every comment that is made
      variables.comment += '<br><span style="color:white">System note: sent from Harbor ref-46947</span>'

      await createComment({ variables })
      resetForm()
      toggle()
    } catch (err) {
      console.log(err)
    } finally {
      setSubmitting(false)
    }
  }

  const commentError = form.touched.comment ? form.errors.comment : ''
  const disabled = isSubmitting || !!Object.keys(form.errors).length

  return (
    <Drawer isOpen={isOpen} ariaLabelledBy="reply-modal-title">
      <Container>
        <Head>
          <Title id="reply-modal-title">Reply</Title>
          <CloseButton
            type="button"
            onClick={toggle}
            aria-label="Close modal"
          >
            <Icon aria-hidden="true" />
          </CloseButton>
        </Head>
        <Col>
          <Label id="comment-label">Comment</Label>
          <Editor
            onChange={value => onChange('comment', value)}
            aria-labelledby="comment-label"
            aria-invalid={!!commentError}
            aria-describedby={commentError ? 'comment-error' : undefined}
          />
          {commentError && <ErrorMsg id="comment-error" role="alert">{commentError}</ErrorMsg>}
          <Wrapper>
            <Button disabled={disabled} isSubmitting={isSubmitting} onClick={onSubmit}>
              Add Comment
            </Button>
          </Wrapper>
          <Checkbox
            label='Please consider this solved'
            value={form.values.solved}
            onChange={() => onChange('solved', !form.values.solved)}
          />
        </Col>
      </Container>
    </Drawer>
  )
}

export default SupportCaseViewForm
